/* eslint-disable @typescript-eslint/no-unused-vars */
// src/pages/PrivacyPolicy.tsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDarkMode } from "../context/DarkModeContext";

const PrivacyPolicy: React.FC = () => {
  const { isDark } = useDarkMode();
  useNavigate();

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? 'bg-gray-900 text-gray-100'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900'
      }`}
    >
      <Helmet>
        <title>Privacy Policy | FindWork Kenya</title>
        <meta
          name="description"
          content="Read FindWork's privacy policy to understand how we handle your personal data, payments, and account information."
        />
        <link rel="canonical" href="https://findwork.ltd/privacy-policy" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className={`text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r ${
              isDark
                ? 'from-blue-400 to-blue-600 text-white'
                : 'from-sky-500 to-blue-600 text-transparent bg-clip-text'
            }`}
          >
            Privacy Policy
          </h1>
          <p
            className={`text-lg ${isDark ? 'text-gray-400' : 'text-blue-700'}`}
          >
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Content Container */}
        <div
          className={`rounded-xl shadow-lg p-6 sm:p-8 ${
            isDark
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-blue-200 shadow-blue-100'
          }`}
        >
          {/* Introduction */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              1. Introduction
            </h2>
            <p
              className={`mb-4 leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              At FindWork ("we," "our," or "us"), we are committed to protecting
              your privacy and ensuring the security of your personal
              information. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our job
              hunting platform.
            </p>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              By using FindWork, you consent to the data practices described in
              this policy. If you do not agree with the terms of this Privacy
              Policy, please do not access the platform.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              2. Information We Collect
            </h2>
            <div
              className={`space-y-4 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <div>
                <h3
                  className={`text-lg font-medium mb-2 ${
                    isDark ? 'text-gray-200' : 'text-blue-700'
                  }`}
                >
                  Personal Information
                </h3>
                <ul
                  className={`list-disc list-inside space-y-2 ml-4 ${
                    isDark ? 'text-gray-400' : 'text-blue-800'
                  }`}
                >
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Contact Information:
                    </strong>{' '}
                    Name, email address, phone number, physical address
                  </li>
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Professional Information:
                    </strong>{' '}
                    Resume/CV, work experience, education history, skills,
                    certifications
                  </li>
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Account Information:
                    </strong>{' '}
                    Username, password, profile preferences
                  </li>
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Payment Information:
                    </strong>{' '}
                    Billing address, payment method details (processed securely
                    by our payment providers)
                  </li>
                </ul>
              </div>

              <div>
                <h3
                  className={`text-lg font-medium mb-2 ${
                    isDark ? 'text-gray-200' : 'text-blue-700'
                  }`}
                >
                  Automatically Collected Information
                </h3>
                <ul
                  className={`list-disc list-inside space-y-2 ml-4 ${
                    isDark ? 'text-gray-400' : 'text-blue-800'
                  }`}
                >
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Device Information:
                    </strong>{' '}
                    IP address, browser type, operating system, device type
                  </li>
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Usage Data:
                    </strong>{' '}
                    Pages visited, features used, time spent on platform, click
                    patterns
                  </li>
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Location Data:
                    </strong>{' '}
                    General location information based on IP address
                  </li>
                  <li>
                    <strong
                      className={isDark ? 'text-gray-200' : 'text-blue-800'}
                    >
                      Cookies and Tracking:
                    </strong>{' '}
                    Information from cookies, web beacons, and similar
                    technologies
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              3. How We Use Your Information
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                We use the information we collect for the following purposes:
              </p>
              <ul
                className={`list-disc list-inside space-y-2 ml-4 ${
                  isDark ? 'text-gray-400' : 'text-blue-800'
                }`}
              >
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Platform Operation:
                  </strong>{' '}
                  To create and manage your account, provide job matching
                  services, and facilitate employer connections
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Communication:
                  </strong>{' '}
                  To send job alerts, application updates, and important service
                  notifications
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Personalization:
                  </strong>{' '}
                  To customize your experience and show relevant job
                  recommendations
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Improvement:
                  </strong>{' '}
                  To analyze platform usage and enhance our services and
                  features
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Security:
                  </strong>{' '}
                  To protect against fraud, abuse, and unauthorized access
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Legal Compliance:
                  </strong>{' '}
                  To comply with applicable laws and regulations
                </li>
              </ul>
            </div>
          </section>

          {/* Information Sharing */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              4. Information Sharing and Disclosure
            </h2>
            <div
              className={`space-y-4 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                We may share your information in the following circumstances:
              </p>

              <div>
                <h3
                  className={`text-lg font-medium mb-2 ${
                    isDark ? 'text-gray-200' : 'text-blue-700'
                  }`}
                >
                  With Employers
                </h3>
                <p
                  className={`mb-2 ${
                    isDark ? 'text-gray-400' : 'text-blue-800'
                  }`}
                >
                  When you apply for jobs, we share your application materials
                  (resume, cover letter, profile information) with the employers
                  you choose to apply to.
                </p>
              </div>

              <div>
                <h3
                  className={`text-lg font-medium mb-2 ${
                    isDark ? 'text-gray-200' : 'text-blue-700'
                  }`}
                >
                  Service Providers
                </h3>
                <p
                  className={`mb-2 ${
                    isDark ? 'text-gray-400' : 'text-blue-800'
                  }`}
                >
                  We may share information with trusted third-party service
                  providers who assist us in operating our platform, such as:
                </p>
                <ul
                  className={`list-disc list-inside space-y-1 ml-4 ${
                    isDark ? 'text-gray-400' : 'text-blue-800'
                  }`}
                >
                  <li>Payment processors (Stripe, PayPal, M-Pesa)</li>
                  <li>Cloud hosting providers</li>
                  <li>Analytics and monitoring services</li>
                  <li>Customer support platforms</li>
                </ul>
              </div>

              <div>
                <h3
                  className={`text-lg font-medium mb-2 ${
                    isDark ? 'text-gray-200' : 'text-blue-700'
                  }`}
                >
                  Legal Requirements
                </h3>
                <p className={isDark ? 'text-gray-400' : 'text-blue-800'}>
                  We may disclose information when required by law, to protect
                  our rights, or to ensure the safety of our users and the
                  public.
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              5. Data Security
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                We implement appropriate technical and organizational security
                measures to protect your personal information, including:
              </p>
              <ul
                className={`list-disc list-inside space-y-2 ml-4 ${
                  isDark ? 'text-gray-400' : 'text-blue-800'
                }`}
              >
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Regular security assessments and vulnerability testing</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Secure data backup and disaster recovery procedures</li>
                <li>Employee training on data protection practices</li>
              </ul>
              <p
                className={`mt-3 ${isDark ? 'text-gray-400' : 'text-blue-800'}`}
              >
                While we strive to protect your information, no method of
                transmission over the Internet or electronic storage is 100%
                secure. We cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              6. Your Data Protection Rights
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                Depending on your location, you may have the following rights
                regarding your personal information:
              </p>
              <ul
                className={`list-disc list-inside space-y-2 ml-4 ${
                  isDark ? 'text-gray-400' : 'text-blue-800'
                }`}
              >
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Access:
                  </strong>{' '}
                  Request copies of your personal data
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Rectification:
                  </strong>{' '}
                  Request correction of inaccurate or incomplete data
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Erasure:
                  </strong>{' '}
                  Request deletion of your personal data
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Restriction:
                  </strong>{' '}
                  Request limitation of processing your data
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Portability:
                  </strong>{' '}
                  Request transfer of your data to another organization
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Objection:
                  </strong>{' '}
                  Object to processing of your personal data
                </li>
                <li>
                  <strong
                    className={isDark ? 'text-gray-200' : 'text-blue-800'}
                  >
                    Withdraw Consent:
                  </strong>{' '}
                  Withdraw previously given consent at any time
                </li>
              </ul>
              <p
                className={`mt-3 ${isDark ? 'text-gray-400' : 'text-blue-800'}`}
              >
                To exercise these rights, please contact us using the
                information provided in the "Contact Us" section.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              7. Cookies and Tracking Technologies
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>We use cookies and similar tracking technologies to:</p>
              <ul
                className={`list-disc list-inside space-y-2 ml-4 ${
                  isDark ? 'text-gray-400' : 'text-blue-800'
                }`}
              >
                <li>Remember your preferences and settings</li>
                <li>Analyze platform usage and performance</li>
                <li>Provide personalized content and job recommendations</li>
                <li>Enable social media features</li>
                <li>Deliver targeted advertising (where permitted)</li>
              </ul>
              <p
                className={`mt-3 ${isDark ? 'text-gray-400' : 'text-blue-800'}`}
              >
                You can control cookies through your browser settings. However,
                disabling cookies may limit your ability to use certain features
                of our platform.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              8. Data Retention
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                We retain your personal information only for as long as
                necessary to:
              </p>
              <ul
                className={`list-disc list-inside space-y-2 ml-4 ${
                  isDark ? 'text-gray-400' : 'text-blue-800'
                }`}
              >
                <li>Fulfill the purposes for which it was collected</li>
                <li>Comply with legal and regulatory requirements</li>
                <li>Resolve disputes and enforce our agreements</li>
                <li>
                  Maintain business records for analysis and audit purposes
                </li>
              </ul>
              <p
                className={`mt-3 ${isDark ? 'text-gray-400' : 'text-blue-800'}`}
              >
                When we no longer need your information, we will securely delete
                or anonymize it.
              </p>
            </div>
          </section>

          {/* International Transfers */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              9. International Data Transfers
            </h2>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              Your information may be transferred to and processed in countries
              other than your own. We ensure appropriate safeguards are in place
              to protect your data during international transfers, including
              standard contractual clauses and adequacy decisions where
              applicable.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              10. Children's Privacy
            </h2>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              FindWork is not intended for children under the age of 18. We do
              not knowingly collect personal information from children under 18.
              If you believe we have collected information from a child under
              18, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              11. Changes to This Privacy Policy
            </h2>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              We may update this Privacy Policy from time to time. We will
              notify you of any material changes by posting the new policy on
              our platform and updating the "Last updated" date. We encourage
              you to review this policy periodically.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              12. Contact Us
            </h2>
            <div
              className={`space-y-2 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our data practices, please contact us:
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Email:
                </strong>{' '}
                privacy@FindWork.com
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Address:
                </strong>{' '}
                [Your Company Address]
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Phone:
                </strong>{' '}
                [Your Contact Number]
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Data Protection Officer:
                </strong>{' '}
                dpo@FindWork.com
              </p>
            </div>
          </section>

          {/* Consent Section */}
          <section
            className={`p-4 rounded-lg ${
              isDark
                ? 'bg-gray-700 border border-gray-600'
                : 'bg-gradient-to-r from-sky-100 to-blue-100 border border-blue-300'
            }`}
          >
            <p
              className={`text-center font-medium ${
                isDark ? 'text-gray-200' : 'text-blue-800'
              }`}
            >
              By using FindWork, you acknowledge that you have read and
              understood this Privacy Policy and consent to our collection, use,
              and sharing of your information as described herein.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div
          className={`mt-8 text-center ${
            isDark ? 'text-gray-400' : 'text-blue-700'
          }`}
        >
          <p className="text-sm">
            Need help?{' '}
            <Link
              to="/contact-support"
              className={`font-medium underline ${
                isDark
                  ? 'text-blue-400 hover:text-blue-300'
                  : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              Contact Support
            </Link>{' '}
            or{' '}
            <Link
              to="/terms-of-service"
              className={`font-medium underline ${
                isDark
                  ? 'text-blue-400 hover:text-blue-300'
                  : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              View Terms of Service
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
